import {Children,Fragment,cloneElement,createContext,isValidElement,useContext,type HTMLAttributes,type ReactElement,type ReactNode,type TableHTMLAttributes} from 'react';
import './responsive-table.css';

type CellProps={children?:ReactNode;colSpan?:number;scope?:string;role?:string;className?:string|undefined};
const numericColumn=(label:string)=>/^(?:amount|balance|total|gross|net|vat|excess|units|flats|due(?: date)?|date|including vat|excluding vat|recorded total)$/i.test(label.trim());
const ColumnLabels=createContext<readonly string[]>([]);

function flatten(children:ReactNode,prefix=''):ReactNode[]{
 return Children.toArray(children).flatMap(child=>{
  if(!isValidElement<CellProps>(child))return [child];
  const key=`${prefix}${child.key}`;
  return child.type===Fragment?flatten(child.props.children,`${key}/`):[cloneElement(child,{key})];
 });
}

function text(children:ReactNode):string{
 return flatten(children).map(child=>isValidElement<CellProps>(child)?text(child.props.children):typeof child==='string'||typeof child==='number'?String(child):'').join(' ').replace(/\s+/g,' ').trim();
}

function interactive(children:ReactNode):boolean{
 return flatten(children).some(child=>isValidElement<CellProps>(child)&&(['button','a','input','select','textarea'].includes(String(child.type))||interactive(child.props.children)));
}

function labelledCells(children:ReactNode,headers:readonly string[]){
 let column=0;
 return flatten(children).map(cell=>{
  if(!isValidElement<CellProps>(cell)||!['th','td'].includes(String(cell.type)))return cell;
  const label=headers[column],span=cell.props.colSpan??1;column+=span;
  return cloneElement(cell,{className:[cell.props.className,span===1&&label&&numericColumn(label)?'column-numeric':undefined].filter(Boolean).join(' ')||undefined,role:cell.props.role??(cell.type==='th'?'rowheader':'cell')},<>{span===1&&label&&<span className="page-table-label" aria-hidden="true" data-column-label={label}/>}{cell.props.children}</>);
 });
}

/** Use inside custom row components so they receive the same labels as native rows. */
export function ResponsiveTableRow({children,...props}:HTMLAttributes<HTMLTableRowElement>){
 return <tr {...props} role={props.role??'row'}>{labelledCells(children,useContext(ColumnLabels))}</tr>;
}

/** Preserve the native table and controls; narrow records repeat their column labels. */
export function ResponsiveTable({children,className='',...props}:TableHTMLAttributes<HTMLTableElement>){
 const sections=flatten(children);
 const head=sections.find(child=>isValidElement(child)&&child.type==='thead') as ReactElement<CellProps>|undefined;
 const headers=head?flatten(head.props.children).flatMap(row=>isValidElement<CellProps>(row)?flatten(row.props.children):[]).flatMap(cell=>isValidElement<CellProps>(cell)?Array.from({length:cell.props.colSpan??1},()=>text(cell.props.children)):[]):[];
 const rows=(content:ReactNode)=>flatten(content).map(row=>{
  if(!isValidElement<CellProps>(row)||row.type!=='tr')return row;
  return cloneElement(row,{role:row.props.role??'row'},labelledCells(row.props.children,headers));
 });
 return <ColumnLabels.Provider value={headers}><div className="page-table-region"><table {...props} role={props.role??'table'} className={`page-table${headers.length>5?' page-table--wide':''} ${className}`}>
  {sections.map(section=>{
   if(!isValidElement<CellProps>(section))return section;
   if(section.type==='thead')return cloneElement(section,{role:section.props.role??'rowgroup',className:[section.props.className,interactive(section.props.children)?'page-table-controls':'page-table-head'].filter(Boolean).join(' ')},flatten(section.props.children).map(row=>{
    if(!isValidElement<CellProps>(row)||row.type!=='tr')return row;
    let column=0;return cloneElement(row,{},flatten(row.props.children).map(cell=>{
      if(!isValidElement<CellProps>(cell))return cell;const label=headers[column];column+=cell.props.colSpan??1;
      return cloneElement(cell,{className:[cell.props.className,label&&numericColumn(label)?'column-numeric':undefined].filter(Boolean).join(' ')||undefined});
    }));
   }));
   if(section.type==='tbody'||section.type==='tfoot')return cloneElement(section,{role:section.props.role??'rowgroup'},rows(section.props.children));
   if(section.type==='tr')return rows([section]);
   return section;
  })}
 </table></div></ColumnLabels.Provider>;
}
